import type { Permissao } from '@locaveri/shared'
import type { QueryClient } from '@tanstack/react-query'
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
} from '@tanstack/react-router'
import type { ReactElement } from 'react'
import { z } from 'zod'

import { AppShell } from '@/components/layout/app-shell'
import { AgendaPage } from '@/pages/agenda-page'
import { LoadingState } from '@/components/state/loading-state'
import { NotFoundState } from '@/components/state/not-found-state'
import { RouteErrorBoundary } from '@/components/state/route-error-boundary'
import { temPermissao } from '@/lib/permissoes'
import { garantirSessaoRestaurada } from '@/lib/sessao'
import { AcessoNegadoPage } from '@/pages/acesso-negado-page'
import { CategoriasPage } from '@/pages/categorias-page'
import { Configurar2faPage } from '@/pages/configurar-2fa-page'
import { ContasPage } from '@/pages/contas-page'
import { CuponsPage } from '@/pages/cupons-page'
import { CustosPage } from '@/pages/custos-page'
import { DisponibilidadePage } from '@/pages/disponibilidade-page'
import { DashboardPage } from '@/pages/dashboard-page'
import { Desafio2faPage } from '@/pages/desafio-2fa-page'
import { EsqueciSenhaPage } from '@/pages/esqueci-senha-page'
import { ExtrasPage } from '@/pages/extras-page'
import { FaturasPage } from '@/pages/faturas-page'
import { FinanceiroPagamentosPage } from '@/pages/financeiro-pagamentos-page'
import { FrotaPage } from '@/pages/frota-page'
import { LocaisPage } from '@/pages/locais-page'
import { LoginPage } from '@/pages/login-page'
import { MapaDaFrotaPage } from '@/pages/mapa-da-frota-page'
import { MultasPage } from '@/pages/multas-page'
import { OcupacaoDaFrotaPage } from '@/pages/ocupacao-da-frota-page'
import { PrecosPage } from '@/pages/precos-page'
import { FilaDeAprovacaoPage } from '@/pages/fila-de-aprovacao-page'
import { PesosDeAlocacaoPage } from '@/pages/pesos-de-alocacao-page'
import { RedefinirSenhaPage } from '@/pages/redefinir-senha-page'
import { RegrasDePrecoPage } from '@/pages/regras-de-preco-page'
import { ReservaPage } from '@/pages/reserva-page'
import { ReservasPage } from '@/pages/reservas-page'
import { RetornoOAuthPage } from '@/pages/retorno-oauth-page'
import { ChecklistPage } from '@/pages/checklist-page'
import { SimuladorPage } from '@/pages/simulador-page'
import { SinistrosPage } from '@/pages/sinistros-page'
import { TemplateDeContratoPage } from '@/pages/template-de-contrato-page'
import { TrocaDeVeiculoPage } from '@/pages/troca-de-veiculo-page'
import { PermissoesPage } from '@/pages/permissoes-page'
import { MiraScorePage } from '@/pages/mira-score-page'
import { PesquisaConfigPage } from '@/pages/pesquisa-config-page'
import { PesquisaRelatorioPage } from '@/pages/pesquisa-relatorio-page'
import { UsuariosPage } from '@/pages/usuarios-page'
import { VeiculoPage } from '@/pages/veiculo-page'
import { VistoriaPage } from '@/pages/vistoria-page'
import {
  estaAutenticado,
  precisaConfigurarTotp,
  temSetupDe2faPendente,
  useAuthStore,
} from '@/stores/auth-store'

/**
 * Roteamento tipado do painel.
 *
 * Rotas declaradas em código (sem geração de arquivo): a árvore inteira cabe em
 * uma tela e o `to` de cada `<Link>` continua verificado pelo TypeScript.
 *
 * ---------------------------------------------------------------------------
 * TRÊS PORTÕES, NESTA ORDEM
 * ---------------------------------------------------------------------------
 *   1. `autenticadaRoute` — tem sessão? Antes de decidir, ESPERA a reidratação
 *      pelo cookie httpOnly. Sem essa espera, todo F5 com sessão válida cairia
 *      em /login, que é o bug clássico de SPA com refresh em cookie.
 *   2. `appRoute` — o segundo fator já está configurado? Papel interno sem TOTP
 *      só enxerga a tela de configuração.
 *   3. cada rota de módulo — a permissão específica (`user.read`, …).
 *
 * O portão nº 3 é DEFESA EM PROFUNDIDADE, não autorização: quem autoriza é a
 * API. Aqui ele evita abrir uma tela que só mostraria 403.
 */

export interface RouterContext {
  /** Disponível nos loaders para pré-carregar query antes de renderizar. */
  readonly queryClient: QueryClient
}

/** `?redirecionar=/usuarios` — para onde voltar depois do login. */
const buscaDoLoginSchema = z.object({
  redirecionar: z.string().optional(),
})

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: function RootLayout(): ReactElement {
    return <Outlet />
  },
  errorComponent: RouteErrorBoundary,
  notFoundComponent: NotFoundState,
})

// ---------------------------------------------------------------------------
// Rotas públicas
// ---------------------------------------------------------------------------

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',

  validateSearch: (
    busca: Record<string, unknown>,
  ): { readonly redirecionar?: string | undefined } => {
    const resultado = buscaDoLoginSchema.safeParse(busca)

    // Parâmetro inválido não derruba a tela de login: ele é ignorado.
    return resultado.success ? resultado.data : {}
  },

  beforeLoad: async () => {
    // Quem já tem sessão não precisa ver o formulário de novo.
    await garantirSessaoRestaurada()

    if (estaAutenticado()) {
      throw redirect({ to: precisaConfigurarTotp() ? '/2fa/configurar' : '/' })
    }
  },

  component: LoginPage,
})

const retornoOAuthRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login/retorno',
  component: RetornoOAuthPage,
})

const esqueciSenhaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/esqueci-senha',
  component: EsqueciSenhaPage,
})

const redefinirSenhaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/redefinir-senha',
  component: RedefinirSenhaPage,
})

const desafio2faRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/2fa',

  beforeLoad: () => {
    // Sem desafio em andamento não há segunda etapa a cumprir. Isso também
    // impede que o endereço seja aberto direto, sem ter passado pela senha.
    if (useAuthStore.getState().desafio === null) {
      throw redirect({ to: '/login' })
    }
  },

  component: Desafio2faPage,
})

// ---------------------------------------------------------------------------
// Rotas autenticadas
// ---------------------------------------------------------------------------

/**
 * Portão nº 1. Sem caminho próprio: existe só para aplicar o guard de sessão a
 * tudo que estiver embaixo.
 */
const autenticadaRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'autenticada',

  beforeLoad: async ({ location }) => {
    await garantirSessaoRestaurada()

    if (!estaAutenticado()) {
      // A ÚNICA exceção do portão: o setup obrigatório de 2FA acontece SEM
      // sessão (o desafio do login é a credencial) — e só nessa rota.
      if (location.pathname.startsWith('/2fa/configurar') && temSetupDe2faPendente()) {
        return
      }
      throw redirect({ to: '/login', search: { redirecionar: location.href } })
    }
  },
})

/**
 * Portão nº 2. A configuração do 2FA fica FORA da casca do painel: quem ainda
 * não configurou não tem o que navegar, e uma sidebar cheia de módulos seria só
 * uma coleção de becos sem saída.
 */
const configurar2faRoute = createRoute({
  getParentRoute: () => autenticadaRoute,
  path: '/2fa/configurar',
  component: Configurar2faPage,
})

const appRoute = createRoute({
  getParentRoute: () => autenticadaRoute,
  id: 'app',

  beforeLoad: () => {
    if (precisaConfigurarTotp()) {
      throw redirect({ to: '/2fa/configurar' })
    }
  },

  component: function LayoutProtegido(): ReactElement {
    return (
      <AppShell>
        <Outlet />
      </AppShell>
    )
  },
})

/** Portão nº 3, usado no `beforeLoad` de cada rota de módulo. */
function exigirPermissao(permissao: Permissao): void {
  if (!temPermissao(permissao)) {
    throw redirect({ to: '/acesso-negado' })
  }
}

const dashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  component: DashboardPage,
})

const usuariosRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/usuarios',
  beforeLoad: () => {
    exigirPermissao('user.read')
  },
  component: UsuariosPage,
})

/**
 * Perfis e permissões.
 *
 * A guarda é `role.read` e não `role.update`: ver quem acessa o quê é auditoria
 * legítima de quem não edita, e a tela já abre em modo leitura quando falta a
 * permissão de escrita.
 */
const permissoesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/permissoes',
  beforeLoad: () => {
    exigirPermissao('role.read')
  },
  component: PermissoesPage,
})

/**
 * Pesquisa de satisfação — o relatório e a configuração.
 *
 * DUAS rotas, e não uma tela com abas, porque as permissões são diferentes:
 * `survey.read` vê o NPS (o ATENDIMENTO tem, é o insumo do próprio trabalho) e
 * `survey.update` configura o limiar de avaliação pública, que é alavanca
 * comercial e fica com o gestor. Uma tela só faria o atendente abrir uma página
 * cuja metade ele não pode usar.
 */
const pesquisaRelatorioRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/pesquisa',
  beforeLoad: () => {
    exigirPermissao('survey.read')
  },
  component: PesquisaRelatorioPage,
})

const pesquisaConfigRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/pesquisa/configuracao',
  beforeLoad: () => {
    // `read` e não `update`: a tela abre em modo leitura para quem não edita, e
    // exigir `update` esconderia a própria configuração de quem precisa
    // conferir o que está valendo antes de pedir a mudança.
    exigirPermissao('survey.read')
  },
  component: PesquisaConfigPage,
})

/**
 * Indicadores (Fase 7).
 *
 * `report.read` e não `dashboard.read`: as duas existem no catálogo e são
 * coisas diferentes. `dashboard.read` é a home do painel, que todo mundo
 * interno vê; `report.read` é análise de rentabilidade — margem por veículo,
 * payback, custo por km. O ATENDIMENTO tem a primeira e não a segunda.
 */
const biRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/indicadores',
  beforeLoad: () => {
    exigirPermissao('report.read')
  },
  // ⚠️ A ÚNICA ROTA CARREGADA SOB DEMANDA — e o motivo é medido, não estético.
  //
  // Ela é a única que usa o Recharts, que sozinho responde por boa parte do
  // bundle. Importá-la junto do resto faria TODO login pagar o download de uma
  // biblioteca de gráficos — inclusive o do atendente, que nem tem
  // `report.read` para abrir a tela.
  //
  // `lazyRouteComponent` do TanStack Router já cuida do Suspense; o guarda de
  // permissão continua em `beforeLoad`, ou seja, ANTES do download.
  component: lazyRouteComponent(() => import('@/pages/bi-page'), 'BiPage'),
})

/**
 * A ocupação da frota SEM dinheiro — a tela da operação.
 *
 * `fleet_occupancy.read`, e não `report.read`: os Indicadores trazem margem por
 * veículo, e o OPERACIONAL é "sem financeiro". Ver o cabeçalho da página.
 */
/**
 * O mapa da frota — onde está cada carro rastreado.
 *
 * `telemetry.read`, a mesma chave da posição na página do veículo: é a mesma
 * informação, para a frota inteira. Nenhum papel além do GESTOR a recebe por
 * padrão (Adendo E).
 */
const mapaDaFrotaRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/mapa',
  beforeLoad: () => {
    exigirPermissao('telemetry.read')
  },
  component: MapaDaFrotaPage,
})

const ocupacaoRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/ocupacao',
  beforeLoad: () => {
    exigirPermissao('fleet_occupancy.read')
  },
  component: OcupacaoDaFrotaPage,
})

const contasRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/contas',
  beforeLoad: () => {
    exigirPermissao('account.read')
  },
  component: ContasPage,
})

// ---------------------------------------------------------------------------
// Fase 2 — frota e categorias
//
// `/frota/$veiculoId` é filha de `/frota` no CAMINHO, mas irmã na árvore: a
// tela de detalhe não reaproveita nada da lista (filtros, paginação, visão em
// cards), e aninhá-la faria a lista renderizar por baixo do detalhe a cada
// abertura. O portão de permissão é o mesmo nas duas.
// ---------------------------------------------------------------------------

const frotaRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/frota',
  beforeLoad: () => {
    exigirPermissao('vehicle.read')
  },
  component: FrotaPage,
})

const veiculoRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/frota/$veiculoId',
  beforeLoad: () => {
    exigirPermissao('vehicle.read')
  },
  component: VeiculoPage,
})

const categoriasRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/categorias',
  beforeLoad: () => {
    exigirPermissao('vehicle_category.read')
  },
  component: CategoriasPage,
})

const locaisRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/locais',
  beforeLoad: () => {
    exigirPermissao('location.read')
  },
  component: LocaisPage,
})

const custosRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/custos',
  beforeLoad: () => {
    exigirPermissao('vehicle_cost.read')
  },
  component: CustosPage,
})

// ---------------------------------------------------------------------------
// Fase 3 — preços e disponibilidade
//
// `simulador` é a tela que o gestor mais usa, e por isso tem rota própria em vez
// de virar aba de `precos`: ele a abre no meio de um atendimento, com o cliente
// esperando, e um endereço direto é a diferença entre responder na hora e
// procurar o caminho.
// ---------------------------------------------------------------------------

const precosRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/precos',
  beforeLoad: () => {
    exigirPermissao('price_table.read')
  },
  component: PrecosPage,
})

const regrasDePrecoRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/precos/regras',
  beforeLoad: () => {
    exigirPermissao('price_table.read')
  },
  component: RegrasDePrecoPage,
})

const simuladorRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/simulador',
  beforeLoad: () => {
    exigirPermissao('quote.create')
  },
  component: SimuladorPage,
})

const extrasRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/extras',
  beforeLoad: () => {
    exigirPermissao('extra.read')
  },
  component: ExtrasPage,
})

const cuponsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/cupons',
  beforeLoad: () => {
    exigirPermissao('coupon.read')
  },
  component: CuponsPage,
})

const disponibilidadeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/disponibilidade',
  beforeLoad: () => {
    exigirPermissao('availability.read')
  },
  component: DisponibilidadePage,
})

// ---------------------------------------------------------------------------
// Fase 4 — reservas e alocação
//
// A fila tem rota própria (e não é aba da lista) pelo mesmo motivo do
// simulador: é a tela que o gestor deixa aberta, e o endereço direto é parte do
// fluxo de trabalho. O portão dela é `reservation.approve` — quem não decide a
// fila não precisa vê-la.
// ---------------------------------------------------------------------------

const reservasRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reservas',
  beforeLoad: () => {
    exigirPermissao('reservation.read')
  },
  component: ReservasPage,
})

const reservaRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reservas/$reservaId',
  beforeLoad: () => {
    exigirPermissao('reservation.read')
  },
  component: ReservaPage,
})

// Vistoria é filha da reserva no CAMINHO e irmã na árvore, como
// `/frota/$veiculoId`: ela não reaproveita nada do detalhe, e o pátio a abre
// direto pelo endereço.
const vistoriaRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reservas/$reservaId/vistorias',
  beforeLoad: () => {
    exigirPermissao('inspection.read')
  },
  component: VistoriaPage,
})

// A troca no meio da locação (Adendo C) exige a permissão do ENDPOINT
// (`allocation.update`): quem não pode trocar não deve nem abrir o fluxo.
const trocaDeVeiculoRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reservas/$reservaId/trocar-veiculo',
  beforeLoad: () => {
    exigirPermissao('allocation.update')
  },
  component: TrocaDeVeiculoPage,
})

// O checklist é configuração do tenant; a leitura basta para abrir a tela
// (a edição é gateada em tela e na API por `inspection.update`).
const checklistRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/vistorias/checklist',
  beforeLoad: () => {
    exigirPermissao('inspection.read')
  },
  component: ChecklistPage,
})

const filaDeAprovacaoRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/fila-de-aprovacao',
  beforeLoad: () => {
    exigirPermissao('reservation.approve')
  },
  component: FilaDeAprovacaoPage,
})

const agendaRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/agenda',
  beforeLoad: () => {
    exigirPermissao('reservation.read')
  },
  component: AgendaPage,
})

const pesosDeAlocacaoRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/alocacoes/pesos',
  beforeLoad: () => {
    exigirPermissao('rental_policy.read')
  },
  component: PesosDeAlocacaoPage,
})

const multasRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/multas',
  beforeLoad: () => {
    exigirPermissao('traffic_fine.read')
  },
  component: MultasPage,
})

const sinistrosRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/sinistros',
  beforeLoad: () => {
    exigirPermissao('incident.read')
  },
  component: SinistrosPage,
})

// ---------------------------------------------------------------------------
// Fase 5 — pagamentos e faturas
//
// As duas telas do financeiro. Capturar/estornar/baixar vivem no DETALHE da
// reserva (perto do contexto que os justifica); `/pagamentos` é a vigília —
// cauções a renovar e reconciliação — e `/faturas` é o ciclo mensal do
// corporativo. Os portões são os das permissões dos endpoints.
// ---------------------------------------------------------------------------

const faturasRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/faturas',
  beforeLoad: () => {
    exigirPermissao('invoice.read')
  },
  component: FaturasPage,
})

const pagamentosRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/pagamentos',
  beforeLoad: () => {
    exigirPermissao('payment.read')
  },
  component: FinanceiroPagamentosPage,
})

// O template é configuração do TENANT (a permissão é `tenant.*`, não
// `contract.*`): mudá-lo muda todos os contratos gerados dali em diante.
const templateDeContratoRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/contratos/template',
  beforeLoad: () => {
    exigirPermissao('tenant.read')
  },
  component: TemplateDeContratoPage,
})

// A chave do Mira Score é da conta da LOCADORA no Mira (quem paga as
// consultas). Ler a situação pede `credit_analysis.read`; trocar a chave,
// `credit_analysis.update` — conferido dentro da tela e na API.
const miraScoreRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/configuracoes/mira-score',
  beforeLoad: () => {
    exigirPermissao('credit_analysis.read')
  },
  component: MiraScorePage,
})

const acessoNegadoRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/acesso-negado',
  component: AcessoNegadoPage,
})

const routeTree = rootRoute.addChildren([
  loginRoute,
  retornoOAuthRoute,
  esqueciSenhaRoute,
  redefinirSenhaRoute,
  desafio2faRoute,
  autenticadaRoute.addChildren([
    configurar2faRoute,
    appRoute.addChildren([
      dashboardRoute,
      usuariosRoute,
      permissoesRoute,
      biRoute,
      ocupacaoRoute,
      mapaDaFrotaRoute,
      pesquisaRelatorioRoute,
      pesquisaConfigRoute,
      contasRoute,
      frotaRoute,
      veiculoRoute,
      categoriasRoute,
      locaisRoute,
      custosRoute,
      precosRoute,
      regrasDePrecoRoute,
      simuladorRoute,
      extrasRoute,
      cuponsRoute,
      disponibilidadeRoute,
      reservasRoute,
      reservaRoute,
      vistoriaRoute,
      trocaDeVeiculoRoute,
      checklistRoute,
      filaDeAprovacaoRoute,
      agendaRoute,
      pesosDeAlocacaoRoute,
      multasRoute,
      sinistrosRoute,
      faturasRoute,
      pagamentosRoute,
      templateDeContratoRoute,
      miraScoreRoute,
      acessoNegadoRoute,
    ]),
  ]),
])

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    // Estados obrigatórios da DoD, aplicados a QUALQUER rota que não declare
    // os seus: nunca existe tela branca entre navegações.
    defaultPendingComponent: function PendingRoute(): ReactElement {
      return (
        <div className="mx-auto w-full max-w-4xl p-6">
          <LoadingState rotulo="Carregando a página…" />
        </div>
      )
    },
    defaultErrorComponent: RouteErrorBoundary,
    defaultNotFoundComponent: NotFoundState,
  })
}

export type AppRouter = ReturnType<typeof createAppRouter>

declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter
  }
}
